import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import GlobalContactsContext from '@totem/components/administration/globalContacts/GlobalContactsContext';
import {
  GlobalContact,
  GlobalContactPayload,
} from '@totem/components/administration/globalContacts/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { GLOBAL_CONTACTS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  children: ReactNode;
};

const GlobalContactsContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [contacts, setContacts] = useState<GlobalContact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingContact, setEditingContact] = useState<GlobalContact | null>(
    null,
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(GLOBAL_CONTACTS_ENDPOINT, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      });
      if (!CheckResponseShowError(response)) {
        setContacts([]);
        return;
      }
      const result: GlobalContact[] = await response.json();
      setContacts(Array.isArray(result) ? result : []);
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upsertContact = useCallback(
    async (contact: GlobalContactPayload) => {
      setSaving(true);
      try {
        const response = await fetch(GLOBAL_CONTACTS_ENDPOINT, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(contact),
        });
        if (!CheckResponseShowError(response)) {
          return;
        }
        notification.success({
          message: contact.id ? 'Contact updated' : 'Contact added',
        });

        setModalOpen(false);

        setEditingContact(null);

        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh, getApiToken],
  );

  const deleteContact = useCallback(
    async (contactId: string) => {
      setSaving(true);
      try {
        const response = await fetch(
          `${GLOBAL_CONTACTS_ENDPOINT}/${contactId}`,
          {
            method: 'DELETE',
            headers: new Headers({
              Authorization: `Bearer ${getApiToken()}`,
            }),
          },
        );
        if (!CheckResponseShowError(response)) {
          return;
        }
        notification.success({ message: 'Contact deleted' });

        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh, getApiToken],
  );

  const openCreateModal = () => {
    setEditingContact(null);

    setModalOpen(true);
  };

  const openEditModal = (contact: GlobalContact) => {
    setEditingContact(contact);

    setModalOpen(true);
  };

  const closeModal = () => {
    setEditingContact(null);

    setModalOpen(false);
  };

  return (
    <GlobalContactsContext.Provider
      value={{
        contacts,
        loading,
        saving,
        modalOpen,
        editingContact,
        refresh,
        openCreateModal,
        openEditModal,
        closeModal,
        upsertContact,
        deleteContact,
      }}
    >
      {children}
    </GlobalContactsContext.Provider>
  );
};

export default GlobalContactsContainer;
